import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { BackendAPI } from '@/services/backend-api'
import { Contract, User } from '@/types/schemas'

interface OnUserLoginParams {
  username: string
  password: string
}

interface OnClientLoginParams {
  asset_number: string
  contract_password: string
}

interface Tokens {
  token: string | undefined
  refresh_token: string | undefined
}

interface StoreState extends Tokens {
  user?: User
  client?: {
    contract: Contract
    asset: {
      id: string
      assetNumber: string
    }
  }
}

interface StoreActions {
  onUserLogin: (props: OnUserLoginParams) => Promise<void>
  onClientLogin: (props: OnClientLoginParams) => Promise<void>
  onGetTokens: () => Tokens
  onRefreshTokens: () => Promise<{ token: string | undefined }>
  onLogout: () => void
}

export const useAuthStore = create(
  persist<StoreState & StoreActions>(
    (set, get) => ({
      user: undefined,
      client: undefined,
      token: undefined,
      refresh_token: undefined,

      /**
       * Login user and update store
       * - Save user data and tokens in store
       */
      onUserLogin: async (body) => {
        const { user, token, refresh_token } =
          await BackendAPI.Authentication.Login(body)
        set(() => ({
          user,
          client: undefined,
          token,
          refresh_token,
        }))
      },

      /**
       * Login user and update store
       * - Save user data and tokens in store
       */
      onClientLogin: async (body) => {
        const { asset, contract, token, refresh_token } =
          await BackendAPI.Authentication.LoginClient(body)

        set(() => ({
          user: undefined,
          client: {
            asset: {
              id: asset.id,
              assetNumber: asset.assetNumber,
            },
            contract,
          },
          token,
          refresh_token,
        }))
      },

      /**
       * Get tokens from store
       * - Used to send tokens in requests
       */
      onGetTokens: () => {
        const { token, refresh_token } = get()
        return { token, refresh_token }
      },

      /**
       * Refresh token and update store
       * - If refresh token is invalid, logout user
       */
      onRefreshTokens: async () => {
        const { refresh_token, onLogout } = get()

        try {
          if (!refresh_token) throw new Error('Token inválido')

          const { token, refresh_token: new_refresh_token } =
            await BackendAPI.Authentication.RefreshToken({ refresh_token })

          set(() => ({ token, refresh_token: new_refresh_token }))

          return { token }
        } catch (error) {
          onLogout()
          throw error
        }
      },

      /**
       * Remove user data from store
       */
      onLogout: () => {
        set(() => ({
          user: undefined,
          client: undefined,
          token: undefined,
          refresh_token: undefined,
        }))
      },
    }),
    { name: 'auth' },
  ),
)
